import React, { useEffect, useRef, useState } from 'react';
import { AccessibilityInfo, KeyboardAvoidingView, Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Text } from './Texte';
import { ScreenHeader } from './ScreenHeader';
import { BarreSegments } from './BarreSegments';
import { ChampEtiquete } from './ChampEtiquete';
import { GradientButton } from './GradientButton';
import { PiedAction } from './PiedAction';
import { FondNuit } from './FondNuit';
import { useGabarit } from '../hooks/useGabarit';
import { OutputLanguagePicker } from './OutputLanguagePicker';
import { LanguagePicker } from './LanguagePicker';
import { ExperienceEditor } from './ExperienceEditor';
import { EducationEditor } from './EducationEditor';
import { ProjectEditor } from './ProjectEditor';
import { TagEditor } from './TagEditor';
import { Briefcase, Globe, Pencil, Upload } from './icons';
import { fleche } from '../i18n/sens';
import { colonneFormulaire, colors, formes, radius, screenPadding, shadow, spacing, typography } from '../theme/tokens';
import { t } from '../i18n';
import { CV_CONTRACT_TYPES } from '../types';
import type {
  CvContractType,
  EducationEntry,
  ExperienceEntry,
  LanguageEntry,
  OutputLanguage,
  ProjectEntry,
} from '../types';

export const ETAPES_CV = 3;

// Un projet ajouté mais resté vide ne compte pas comme « Ajouté ».
const projetRempli = (p: ProjectEntry) =>
  Boolean(p.name.trim() || p.description.trim() || p.date.month || p.date.year);

// Tout ce que la personne saisit, hors affichage. L'écran le tient, ce composant le montre.
export interface CvFormValues {
  name: string;
  role: string;
  // « Tu cherches… » : facultatif, null = non précisé.
  contractType: CvContractType | null;
  extra: string;
  outputLanguage: OutputLanguage;
  education: EducationEntry[];
  experiences: ExperienceEntry[];
  projects: ProjectEntry[];
  skills: string[];
  interests: string[];
  languages: LanguageEntry[];
}

export interface CvCreateFormProps {
  // 0, 1 ou 2 : « 1 sur 3 » à « 3 sur 3 ».
  etape: number;
  valeurs: CvFormValues;
  onChange: (patch: Partial<CvFormValues>) => void;
  // Nom que le profil fournit déjà pour l'en-tête du CV ; vide quand le profil n'en a pas.
  nomDuProfil: string;
  canGenerate: boolean;
  loading: boolean;
  error: string | null;
  onBack?: () => void;
  onNext: () => void;
  onGenerate: () => void;
  // « Tu as déjà un CV ? » : absent, la carte n'est pas montrée (un lien qui ne mène nulle part).
  onImportCv?: () => void;
}

/**
 * Les trois écrans de saisie de « Créer un CV » : poste et langue, parcours, puis tout le reste
 * (facultatif). Il reste monté d'un écran à l'autre : les rubriques ouvertes le restent, et les
 * saisies vivent dans l'écran parent, donc rien ne se perd en revenant en arrière.
 */
export function CvCreateForm({
  etape,
  valeurs,
  onChange,
  nomDuProfil,
  canGenerate,
  loading,
  error,
  onBack,
  onNext,
  onGenerate,
  onImportCv,
}: CvCreateFormProps) {
  // Chaque écran repart du haut : sans cela, l'écran 2 s'ouvrait au niveau où l'écran 1 avait été quitté.
  const { large } = useGabarit();
  const defilement = useRef<ScrollView>(null);
  useEffect(() => {
    defilement.current?.scrollTo?.({ y: 0, animated: false });
  }, [etape]);

  // Ouvertes d'emblée quand elles contiennent déjà quelque chose (retour du serveur après un refus).
  const [projetOuvert, setProjetOuvert] = useState(valeurs.projects.length > 0);
  const [extraOuvert, setExtraOuvert] = useState(valeurs.extra.trim().length > 0);

  const derniere = etape === ETAPES_CV - 1;
  const posteVide = valeurs.role.trim().length === 0;
  // Sans poste, ni « Continuer » (écran 1) ni « Générer » (écran 3) ne servent : on le dit plutôt
  // que de laisser un bouton grisé sans explication.
  const indice = posteVide && (etape === 0 || derniere) ? t('cvCreateFlow.roleRequired') : null;
  const bloque = etape === 0 ? posteVide : derniere ? !canGenerate : false;

  const titresEtapes = [t('cvCreateFlow.step1Titre'), t('cvCreateFlow.step2Titre'), t('cvCreateFlow.step3Titre')];
  const titre = titresEtapes[etape];
  const sousTitre = [t('cvCreateFlow.step1Sous'), t('cvCreateFlow.step2Sous'), t('cvCreateFlow.step3Sous')][etape];

  // Un lecteur d'écran n'entend pas qu'on a changé d'écran : on le lui dit (pas au premier affichage).
  const premierAffichage = useRef(true);
  useEffect(() => {
    if (premierAffichage.current) {
      premierAffichage.current = false;
      return;
    }
    AccessibilityInfo.announceForAccessibility?.(`${t('cvCreateFlow.etapeSur', { n: etape + 1, total: ETAPES_CV })} - ${titre}`);
  }, [etape, titre]);

  const sTitres = (
    <>
          <View style={styles.titres}>
            <Text accessibilityRole="header" style={styles.titre}>
              {titre}
            </Text>
            <Text testID="cvc-sous-titre" style={styles.sousTitre}>
              {sousTitre}
            </Text>
          </View>

              </>
  );
  const sCorps = (
    <>
{etape === 0 ? (
            <>
              <ChampEtiquete
                testID="cvc-role"
                label={t('cvCreate.questions.role.label')}
                placeholder={t('cvCreate.questions.role.placeholder')}
                value={valeurs.role}
                onChangeText={(role) => onChange({ role })}
              />

              <View style={styles.contrat} testID="cvc-contrat">
                <Text style={styles.contratLabel}>{t('cvCreateFlow.contratLabel')}</Text>
                <View style={styles.contratPastilles}>
                  {CV_CONTRACT_TYPES.map((k) => {
                    const actif = valeurs.contractType === k;
                    return (
                      <Pressable
                        key={k}
                        testID={`cvc-contrat-${k}`}
                        accessibilityRole="button"
                        accessibilityState={{ selected: actif }}
                        accessibilityHint={t('cvCreateFlow.contratHint')}
                        // Un second appui retire le choix : le champ est facultatif.
                        onPress={() => onChange({ contractType: actif ? null : k })}
                        style={[styles.pastille, actif && styles.pastilleActive]}
                      >
                        <Text style={[styles.pastilleTexte, actif && styles.pastilleTexteActif]}>
                          {t(`cvCreateFlow.contrats.${k}`)}
                        </Text>
                      </Pressable>
                    );
                  })}
                </View>
              </View>

              {nomDuProfil ? (
                // Le profil l'emporte déjà sur toute saisie pour l'en-tête du CV : un champ ici
                // laisserait croire qu'il compte.
                <View testID="cvc-name-profil" style={styles.nomProfil}>
                  <View style={styles.nomTextes}>
                    <Text style={styles.nomLabel}>{t('cvCreateFlow.nameLabel')}</Text>
                    <Text style={styles.nomValeur} numberOfLines={1}>
                      {nomDuProfil}
                    </Text>
                  </View>
                  <Text style={styles.nomOrigine}>{t('cvCreateFlow.nameFromProfile')}</Text>
                </View>
              ) : (
                <ChampEtiquete
                  testID="cvc-name"
                  label={t('cvCreateFlow.nameLabel')}
                  placeholder={t('cvCreateFlow.namePlaceholder')}
                  value={valeurs.name}
                  onChangeText={(name) => onChange({ name })}
                />
              )}

              <View style={styles.carte}>
                <View style={styles.langueEntete}>
                  <Globe size={18} color={colors.electricDeep} />
                  <Text style={styles.carteTitre}>{t('cvCreateFlow.languageLabel')}</Text>
                </View>
                <OutputLanguagePicker
                  variante="compact"
                  value={valeurs.outputLanguage}
                  onChange={(outputLanguage) => onChange({ outputLanguage })}
                  label={t('cvCreateFlow.languageLabel')}
                  testID="cvc-language"
                />
                <Text style={styles.indication}>{t('cvCreateFlow.languageHint')}</Text>
              </View>

              {onImportCv ? (
                <Pressable
                  testID="cvc-import"
                  accessibilityRole="button"
                  accessibilityLabel={`${t('cvCreateFlow.importTitre')} ${t('cvCreateFlow.importSous')}`}
                  onPress={onImportCv}
                  style={styles.importer}
                >
                  <View style={styles.importerIcone}>
                    <Upload size={18} color={colors.electricDeep} />
                  </View>
                  <View style={styles.nomTextes}>
                    <Text style={styles.importerTitre}>{t('cvCreateFlow.importTitre')}</Text>
                    <Text style={styles.importerSous}>{t('cvCreateFlow.importSous')}</Text>
                  </View>
                  <Text style={styles.chevron}>{fleche('›')}</Text>
                </Pressable>
              ) : null}
            </>
          ) : null}

          {etape === 1 ? (
            <>
              <EducationEditor
                value={valeurs.education}
                onChange={(education) => onChange({ education })}
                testID="cvc-education"
              />
              <ExperienceEditor
                value={valeurs.experiences}
                onChange={(experiences) => onChange({ experiences })}
                testID="cvc-experiences"
              />
            </>
          ) : null}

          {etape === 2 ? (
            <>
              <View style={styles.carte}>
                <Text style={styles.carteTitre}>{t('cvCreate.questions.skills.label')}</Text>
                <TagEditor
                  value={valeurs.skills}
                  etiquette={t('cvCreate.questions.skills.label')}
                  onChange={(skills) => onChange({ skills })}
                  placeholder={t('cvCreateFlow.skillPlaceholder')}
                  testID="cvc-skills"
                />
              </View>

              <View style={styles.carte}>
                <Text style={styles.carteTitre}>{t('cvCreate.questions.languages.label')}</Text>
                <LanguagePicker
                  value={valeurs.languages}
                  onChange={(languages) => onChange({ languages })}
                  outputLanguage={valeurs.outputLanguage}
                  testID="cvc-languages"
                />
              </View>

              <View style={styles.carte}>
                <Text style={styles.carteTitre}>{t('cvCreate.questions.interests.label')}</Text>
                <TagEditor
                  value={valeurs.interests}
                  etiquette={t('cvCreate.questions.interests.label')}
                  onChange={(interests) => onChange({ interests })}
                  placeholder={t('cvCreateFlow.interestPlaceholder')}
                  testID="cvc-interests"
                />
              </View>

              <View style={styles.tuiles}>
                <Tuile
                  testID="cvc-tuile-projet"
                  icone={<Briefcase size={18} color={colors.electricDeep} />}
                  titre={t('cvCreateFlow.tuileProjet')}
                  sousTitre={valeurs.projects.some(projetRempli) ? t('cvCreateFlow.ajoute') : t('cvCreateFlow.optionnel')}
                  ouverte={projetOuvert}
                  onPress={() => setProjetOuvert((o) => !o)}
                />
                <Tuile
                  testID="cvc-tuile-extra"
                  icone={<Pencil size={18} color={colors.electricDeep} />}
                  titre={t('cvCreateFlow.tuileAutre')}
                  sousTitre={valeurs.extra.trim() ? t('cvCreateFlow.ajoute') : t('cvCreateFlow.optionnel')}
                  ouverte={extraOuvert}
                  onPress={() => setExtraOuvert((o) => !o)}
                />
              </View>

              {projetOuvert ? (
                <ProjectEditor
                  value={valeurs.projects}
                  onChange={(projects) => onChange({ projects })}
                  testID="cvc-projects"
                />
              ) : null}

              {extraOuvert ? (
                <ChampEtiquete
                  testID="cvc-extra"
                  label={t('cvCreateFlow.tuileAutre')}
                  placeholder={t('cvCreate.questions.extra.placeholder')}
                  multiline
                  value={valeurs.extra}
                  onChangeText={(extra) => onChange({ extra })}
                />
              ) : null}
            </>
          ) : null}
    </>
  );
  const sPied = (
    <>
      <PiedAction testID="cvc-actions" enColonne={large}>
        {error && derniere ? (
          // L'échec vient de « Générer » : il se lit là où on a appuyé, pas sur les écrans d'avant.
          // Un refus de quota ouvre le Paywall (useClaude) : seul un échec technique arrive ici.
          <Text testID="cvc-error" style={styles.erreur}>
            {error}
          </Text>
        ) : indice ? (
          <Text testID="cvc-hint" style={styles.indice}>
            {indice}
          </Text>
        ) : null}
        <GradientButton
          variante="cta"
          hauteur="lg"
          withArrow
          label={derniere ? t('cvCreateFlow.generate') : t('cvCreateFlow.next')}
          loading={derniere && loading}
          disabled={bloque}
          onPress={derniere ? onGenerate : onNext}
          testID={derniere ? 'cvc-generate' : 'cvc-next'}
        />
      </PiedAction>
    </>
  );
  const sEtapes = (
    <>
      <Text style={styles.marqueTitreL}>{t('cvCreateFlow.titre')}</Text>
      <View style={styles.etapesL}>
        {titresEtapes.map((libelle, i) => (
          <View key={libelle} style={styles.etapeL}>
            <View style={[styles.etapeNumL, i === etape && styles.etapeNumActiveL, i < etape && styles.etapeNumFaiteL]}>
              <Text style={[styles.etapeNumTexteL, i === etape && styles.etapeNumTexteActiveL]}>{i + 1}</Text>
            </View>
            <Text style={[styles.etapeTitreL, i === etape && styles.etapeTitreActiveL]} numberOfLines={2}>
              {libelle}
            </Text>
          </View>
        ))}
      </View>
    </>
  );

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <ScreenHeader
        variante="carte"
        onBack={onBack}
        testID="cvc-back"
        title={t('cvCreateFlow.titre')}
        droite={
          <Text testID="cvc-etape" style={styles.etape}>
            {t('cvCreateFlow.etapeSur', { n: etape + 1, total: ETAPES_CV })}
          </Text>
        }
      />
      <View style={styles.barre}>
        <BarreSegments testID="cvc-progress" etape={etape + 1} total={ETAPES_CV} />
      </View>

      {/* Ordinateur : les trois étapes à gauche sur fond sombre, la saisie à droite, le bouton sous la saisie.
          Le téléphone garde son écran unique avec sa barre de progression. */}
      {large ? (
        <View testID="cvc-split" style={styles.splitL}>
          <View style={styles.panneauEtapesL}>
            <FondNuit style={StyleSheet.absoluteFill} />
            <View style={styles.etapesContenuL}>{sEtapes}</View>
          </View>
          <View style={styles.panneauFormL}>
            <ScrollView
              ref={defilement}
              contentContainerStyle={styles.contenuL}
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator={false}
            >
              {sTitres}
              {sCorps}
            </ScrollView>
            <View style={styles.piedL}>{sPied}</View>
          </View>
        </View>
      ) : (
        <>
      <KeyboardAvoidingView
        testID="cvc-kav"
        style={styles.flex}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <ScrollView
          ref={defilement}
          contentContainerStyle={[styles.content, colonneFormulaire]}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="on-drag"
          showsVerticalScrollIndicator={false}
        >
            {sTitres}
            {sCorps}
        </ScrollView>
      </KeyboardAvoidingView>

      {sPied}
        </>
      )}
    </SafeAreaView>
  );
}

function Tuile({
  testID,
  icone,
  titre,
  sousTitre,
  ouverte,
  onPress,
}: {
  testID: string;
  icone: React.ReactNode;
  titre: string;
  sousTitre: string;
  ouverte: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      accessibilityState={{ expanded: ouverte }}
      accessibilityLabel={`${titre}, ${sousTitre}`}
      onPress={onPress}
      style={[styles.tuile, ouverte && styles.tuileOuverte]}
    >
      <View style={styles.tuileIcone}>{icone}</View>
      <View style={styles.nomTextes}>
        <Text style={styles.tuileTitre} numberOfLines={2}>
          {titre}
        </Text>
        <Text style={styles.tuileSous}>{sousTitre}</Text>
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  splitL: { flex: 1, flexDirection: 'row' },
  panneauEtapesL: { width: 380, backgroundColor: colors.night, overflow: 'hidden', justifyContent: 'center' },
  etapesContenuL: { padding: spacing.xxl + spacing.xl, gap: spacing.xxl },
  marqueTitreL: { fontSize: 36, lineHeight: 42, fontWeight: '800', letterSpacing: -1, color: colors.white },
  etapesL: { gap: spacing.xl },
  etapeL: { flexDirection: 'row', alignItems: 'center', gap: spacing.md },
  etapeNumL: {
    width: 36,
    height: 36,
    borderRadius: radius.pill,
    borderWidth: 1.5,
    borderColor: colors.bordureNuit,
    alignItems: 'center',
    justifyContent: 'center',
  },
  etapeNumActiveL: { backgroundColor: colors.white, borderColor: colors.white },
  etapeNumFaiteL: { backgroundColor: colors.surfaceNuitForte },
  etapeNumTexteL: { fontSize: typography.bodyMd.size, fontWeight: '800', color: colors.blancAtenue },
  etapeNumTexteActiveL: { color: colors.night },
  etapeTitreL: { flex: 1, fontSize: typography.bodyMd.size + 2, fontWeight: '700', color: colors.blancAtenue },
  etapeTitreActiveL: { color: colors.white, fontWeight: '800' },
  panneauFormL: { flex: 1, minWidth: 0 },
  contenuL: { width: '100%', maxWidth: 680, alignSelf: 'center', padding: spacing.xxl, paddingBottom: spacing.xl, gap: spacing.md },
  piedL: { width: '100%', maxWidth: 680, alignSelf: 'center', paddingHorizontal: spacing.xxl, paddingBottom: spacing.xl, paddingTop: spacing.sm, gap: spacing.sm },
  container: { flex: 1, backgroundColor: colors.grey50 },
  flex: { flex: 1 },
  etape: { fontSize: typography.small.size, fontWeight: '800', color: colors.grey500 },
  barre: { paddingHorizontal: screenPadding, paddingTop: spacing.xs },
  content: { padding: screenPadding, paddingBottom: spacing.xxl, gap: spacing.md },
  titres: { gap: spacing.xs },
  titre: { fontSize: 26, lineHeight: 32, fontWeight: '800', color: colors.night },
  sousTitre: { fontSize: typography.body.size, lineHeight: typography.body.lineHeight, color: colors.grey500 },

  carte: {
    gap: spacing.sm,
    padding: spacing.md,
    borderRadius: formes.rayon.md,
    backgroundColor: colors.white,
    ...shadow.card,
  },
  carteTitre: { fontSize: typography.bodyMd.size, fontWeight: '800', color: colors.night },
  contrat: { gap: spacing.sm },
  contratLabel: { fontSize: typography.small.size, fontWeight: '800', color: colors.night },
  contratPastilles: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  pastille: {
    minHeight: formes.hauteur.sm,
    justifyContent: 'center',
    paddingHorizontal: spacing.md,
    borderRadius: radius.pill,
    backgroundColor: colors.white,
    borderWidth: 1.5,
    borderColor: colors.grey200,
  },
  pastilleActive: { backgroundColor: colors.night, borderColor: colors.night },
  pastilleTexte: { fontSize: typography.small.size, fontWeight: '700', color: colors.grey700 },
  pastilleTexteActif: { color: colors.white },
  langueEntete: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  indication: { fontSize: typography.small.size, lineHeight: typography.small.lineHeight, color: colors.grey500 },

  nomProfil: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
    minHeight: formes.hauteur.md + 4,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: formes.rayon.md,
    backgroundColor: colors.white,
    ...shadow.card,
  },
  nomTextes: { flex: 1, minWidth: 0, gap: 1 },
  nomLabel: { fontSize: typography.caption.size, fontWeight: '700', color: colors.grey700 },
  nomValeur: { fontSize: typography.body.size + 1, fontWeight: '800', color: colors.night },
  nomOrigine: { fontSize: typography.small.size, color: colors.grey500 },

  importer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    minHeight: formes.hauteur.lg,
    padding: spacing.md,
    borderRadius: formes.rayon.md,
    borderWidth: 1.5,
    borderStyle: 'dashed',
    borderColor: colors.bordureClaire,
  },
  importerIcone: {
    width: 40,
    height: 40,
    borderRadius: formes.rayon.sm,
    backgroundColor: colors.blueSoft,
    alignItems: 'center',
    justifyContent: 'center',
  },
  importerTitre: { fontSize: typography.bodyMd.size, fontWeight: '800', color: colors.night },
  importerSous: { fontSize: typography.small.size, color: colors.grey500 },
  chevron: { fontSize: 22, color: colors.grey500 },

  tuiles: { flexDirection: 'row', gap: spacing.sm },
  tuile: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    minHeight: formes.hauteur.lg,
    padding: spacing.md,
    borderRadius: formes.rayon.md,
    borderWidth: 1.5,
    borderColor: 'transparent',
    backgroundColor: colors.white,
    ...shadow.card,
  },
  tuileOuverte: { borderColor: colors.electricDeep },
  tuileIcone: {
    width: 36,
    height: 36,
    borderRadius: formes.rayon.sm,
    backgroundColor: colors.blueSoft,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tuileTitre: { fontSize: typography.body.size, fontWeight: '800', color: colors.night },
  tuileSous: { fontSize: typography.small.size, color: colors.grey500 },

  erreur: { textAlign: 'center', fontSize: typography.small.size, color: colors.error },
  indice: { textAlign: 'center', fontSize: typography.small.size, color: colors.grey500 },
});
